/**
 * Mock floating analytics dashboard — pure CSS/SVG, no real data.
 * Counts up once on mount, draws sparkline, animates donut.
 */
import { useEffect, useState } from "react";
import { useCountUp } from "@/hooks/useCountUp";

const sparkline = "M0,60 L20,55 L40,48 L60,52 L80,40 L100,32 L120,38 L140,28 L160,20 L180,24 L200,12";

export const FloatingDashboard = () => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setEnabled(true), 200);
    return () => window.clearTimeout(id);
  }, []);

  const impressions = useCountUp(248, { enabled, duration: 1600 });
  const clicks = useCountUp(14, { enabled, duration: 1400, decimals: 1 });
  const ctr = useCountUp(563, { enabled, duration: 1500 });
  const score = useCountUp(98, { enabled, duration: 1400 });

  return (
    <div className="glass-strong p-5 md:p-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs font-semibold text-foreground/70 tracking-widest uppercase">Live · Overview</span>
        </div>
        <span className="text-[10px] font-medium text-muted-foreground bg-muted/60 px-2 py-1 rounded-md">Last 7 Days</span>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <KPI label="Impressions" value={`${(impressions / 10).toFixed(1)}M`} delta="+12.5%" />
        <KPI label="Clicks" value={`${clicks}M`} delta="+8.2%" />
        <KPI label="CTR" value={`${(ctr / 100).toFixed(2)}%`} delta="+9.7%" />
      </div>

      {/* Sparkline + AI score */}
      <div className="grid grid-cols-5 gap-3">
        <div className="col-span-3 rounded-xl bg-muted/40 p-3 border border-border/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] text-muted-foreground font-medium">Spend · 3.7M SAR</span>
            <span className="text-[10px] text-emerald-500 font-semibold">+11.3%</span>
          </div>
          <svg viewBox="0 0 200 70" className="w-full h-14">
            <defs>
              <linearGradient id="spark-fill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.45" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={`${sparkline} L200,70 L0,70 Z`} fill="url(#spark-fill)" />
            <path
              d={sparkline}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="draw-once"
              style={{ ["--len" as never]: "260" }}
            />
          </svg>
        </div>

        {/* AI score donut */}
        <div className="col-span-2 rounded-xl bg-muted/40 p-3 border border-border/50 flex items-center gap-3">
          <svg viewBox="0 0 60 60" className="w-14 h-14 -rotate-90">
            <circle cx="30" cy="30" r="24" fill="none" stroke="hsl(var(--muted))" strokeWidth="6" />
            <circle
              cx="30"
              cy="30"
              r="24"
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${(score / 100) * 150.8} 150.8`}
              style={{ transition: "stroke-dasharray 1.5s ease" }}
            />
          </svg>
          <div>
            <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">AI Score</div>
            <div className="text-2xl font-bold holo-num leading-none">{score}%</div>
            <div className="text-[10px] text-emerald-500 font-semibold mt-0.5">Excellent</div>
          </div>
        </div>
      </div>

      {/* Bottom row: top cities */}
      <div className="mt-4 rounded-xl bg-muted/40 p-3 border border-border/50">
        <div className="text-[11px] text-muted-foreground font-medium mb-2">Top Cities</div>
        <div className="space-y-1.5">
          {[
            { city: "Riyadh", pct: 40 },
            { city: "Jeddah", pct: 25 },
            { city: "Dammam", pct: 15 },
            { city: "Makkah", pct: 10 },
          ].map((row, i) => (
            <div key={row.city} className="flex items-center gap-2 text-[11px]">
              <span className="w-14 text-foreground/80 font-medium">{row.city}</span>
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-primary-light rounded-full"
                  style={{
                    width: enabled ? `${row.pct}%` : "0%",
                    transition: `width 1200ms cubic-bezier(.2,.8,.2,1) ${300 + i * 120}ms`,
                  }}
                />
              </div>
              <span className="w-9 text-right text-muted-foreground tabular-nums">{row.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const KPI = ({ label, value, delta }: { label: string; value: string; delta: string }) => (
  <div className="rounded-xl bg-muted/40 p-3 border border-border/50">
    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">{label}</div>
    <div className="text-xl font-bold holo-num mt-1 leading-none">{value}</div>
    <div className="text-[10px] text-emerald-500 font-semibold mt-1">{delta}</div>
  </div>
);
